import React, { useState } from 'react';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { Chip } from 'primereact/chip';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';

export const SP_SKIP = new Set([
    'client_id', 'status_code', 'success', 'message', 'message_code',
    'error', 'is_asynchronous', 'transaction_id', 'request_id',
]);

export const prettyLabel = (k) =>
    String(k).replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

export const isScalar = (v) =>
    v === null || v === undefined ||
    typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';

export const statusSeverity = (v) => {
    const s = String(v).toLowerCase();
    if (s.includes('dispose') || s.includes('close') || s.includes('decided') ||
        s === 'active' || s === 'valid' || s === 'success') return 'success';
    if (s.includes('pending') || s.includes('inactive') || s.includes('not')) return 'warning';
    if (s.includes('reject') || s.includes('dismiss') || s.includes('expired') ||
        s.includes('cancelled') || s.includes('suspended') || s.includes('fail')) return 'danger';
    return 'info';
};

const isCurrencyKey = (k) => /amount|capital|income|credit|balance|sanctioned|disbursed|turnover|investment|price/i.test(k);
const isStatusKey = (k) => /(status|stage|nature|result)(?:_|$)|^(status|stage)/i.test(k);

export const formatScalar = (v, keyName = '') => {
    if (v === null || v === undefined || v === '') return <span className="text-500">—</span>;
    if (typeof v === 'boolean') return <Tag value={v ? 'Yes' : 'No'} severity={v ? 'success' : 'info'} />;
    if (typeof v === 'number' && isCurrencyKey(keyName)) return '₹' + v.toLocaleString('en-IN');
    return String(v);
};

export const SmartField = ({ name, value, highlight }) => {
    if (value === null || value === undefined || value === '') return null;

    const isLong = isScalar(value) && String(value ?? '').length > 120;
    const isStatus = isStatusKey(name) && typeof value === 'string';

    if (isLong) {
        return (
            <div className="col-12 flex flex-column gap-1">
                <span className="text-500 text-xs font-medium">{prettyLabel(name)}</span>
                <div
                    className="surface-100 border-round border-left-3 border-primary text-700 text-sm line-height-3 p-3"
                    style={{ maxHeight: '200px', overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                >
                    {String(value)}
                </div>
            </div>
        );
    }

    return (
        <div className="col-12 sm:col-6 lg:col-4 flex flex-column gap-1">
            <span className="text-500 text-xs font-medium">{prettyLabel(name)}</span>
            <span
                className={highlight
                    ? 'text-primary text-sm font-bold font-mono'
                    : 'text-900 text-sm font-semibold'
                }
                style={{ wordBreak: 'break-word' }}
            >
                {isStatus ? <Tag value={String(value)} severity={statusSeverity(value)} /> : formatScalar(value, name)}
            </span>
        </div>
    );
};

export const SmartSection = ({ title, icon, data, highlightKeys = [] }) => {
    const [expanded, setExpanded] = useState({});

    if (!data || (typeof data === 'object' && Object.keys(data).length === 0)) return null;

    const entries = Object.entries(data).filter(
        ([k, v]) => !SP_SKIP.has(k) && v !== null && v !== undefined && v !== ''
    );
    if (entries.length === 0) return null;

    const scalars = entries.filter(([, v]) => isScalar(v));
    const arrays = entries.filter(([, v]) => Array.isArray(v) && v.length > 0);
    const objects = entries.filter(
        ([, v]) => typeof v === 'object' && !Array.isArray(v) && v !== null && Object.keys(v).length > 0
    );

    return (
        <div className="pt-3 pb-3 border-bottom-1 surface-border">
            {title && (
                <div className="flex align-items-center gap-2 mb-3">
                    {icon && <i className={`pi ${icon} text-primary`} aria-hidden="true" />}
                    <span className="text-sm font-semibold text-900 uppercase">{title}</span>
                </div>
            )}

            {scalars.length > 0 && (
                <div className="grid">
                    {scalars.map(([k, v]) => (
                        <SmartField key={k} name={k} value={v} highlight={highlightKeys.includes(k)} />
                    ))}
                </div>
            )}

            {arrays.map(([k, v]) => {
                const isObjectArray = typeof v[0] === 'object' && v[0] !== null;
                return (
                    <div key={k} className="mt-3">
                        <div className="flex align-items-center gap-2 mb-2">
                            <i className="pi pi-list text-primary text-sm" aria-hidden="true" />
                            <span className="text-xs font-semibold text-700 uppercase">
                                {prettyLabel(k)} ({v.length})
                            </span>
                        </div>
                        {isObjectArray ? (
                            <DataTable
                                value={v}
                                size="small"
                                stripedRows
                                showGridlines
                                responsiveLayout="scroll"
                                paginator={v.length > 10}
                                rows={10}
                                className="p-datatable-sm p-datatable-gridlines mt-2"
                            >
                                {Object.keys(v[0]).filter((key) => isScalar(v[0][key])).map((key) => (
                                    <Column
                                        key={key}
                                        field={key}
                                        header={prettyLabel(key)}
                                        body={(row) => {
                                            const val = row[key];
                                            if (isStatusKey(key) && typeof val === 'string') {
                                                return <Tag value={val} severity={statusSeverity(val)} />;
                                            }
                                            return formatScalar(val, key);
                                        }}
                                    />
                                ))}
                            </DataTable>
                        ) : (
                            <div className="flex flex-wrap gap-2 mt-2">
                                {v.map((item, i) => <Chip key={i} label={String(item)} />)}
                            </div>
                        )}
                    </div>
                );
            })}

            {objects.map(([k, v]) => {
                const isOpen = expanded[k] !== false;
                const toggleId = `nested-${k}`;
                return (
                    <div key={k} className="mt-3">
                        <button
                            type="button"
                            aria-expanded={isOpen}
                            aria-controls={toggleId}
                            className="flex align-items-center gap-2 mb-2 w-full p-0 cursor-pointer"
                            style={{ background: 'transparent', border: 'none', textAlign: 'left' }}
                            onClick={() => setExpanded((prev) => ({ ...prev, [k]: !isOpen }))}
                        >
                            <i className={`pi ${isOpen ? 'pi-folder-open' : 'pi-folder'} text-primary text-sm`} aria-hidden="true" />
                            <span className="text-xs font-semibold text-700 uppercase">{prettyLabel(k)}</span>
                            <i
                                className={`pi ${isOpen ? 'pi-chevron-down' : 'pi-chevron-right'} text-500 text-xs ml-auto`}
                                aria-hidden="true"
                            />
                        </button>
                        {isOpen && (
                            <div id={toggleId} className="surface-100 border-round p-3 mt-2">
                                <div className="grid">
                                    {Object.entries(v)
                                        .filter(([, iv]) => iv !== null && iv !== undefined && iv !== '')
                                        .map(([ik, iv]) => {
                                            if (isScalar(iv)) {
                                                return <SmartField key={ik} name={ik} value={iv} />;
                                            }
                                            if (Array.isArray(iv) && iv.length > 0) {
                                                return (
                                                    <div key={ik} className="col-12 flex flex-column gap-1">
                                                        <span className="text-500 text-xs font-medium">{prettyLabel(ik)}</span>
                                                        <div className="flex flex-wrap gap-1 mt-1">
                                                            {iv.map((item, i) => (
                                                                <Chip
                                                                    key={i}
                                                                    label={typeof item === 'object' ? JSON.stringify(item) : String(item)}
                                                                    className="text-xs"
                                                                />
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            }
                                            if (typeof iv === 'object') {
                                                return (
                                                    <div key={ik} className="col-12 flex flex-column gap-1">
                                                        <span className="text-500 text-xs font-medium">{prettyLabel(ik)}</span>
                                                        <div className="surface-0 border-round p-3 mt-1">
                                                            <div className="grid">
                                                                {Object.entries(iv)
                                                                    .filter(([, iiv]) => iiv !== null && iiv !== undefined && iiv !== '' && isScalar(iiv))
                                                                    .map(([iik, iiv]) => (
                                                                        <SmartField key={iik} name={iik} value={iiv} />
                                                                    ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            }
                                            return null;
                                        })}
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export const RawResponseButton = ({ data }) => {
    const [visible, setVisible] = useState(false);
    return (
        <>
            <div className="pt-3 text-center">
                <Button
                    label="View raw response"
                    icon="pi pi-code"
                    size="small"
                    severity="secondary"
                    outlined
                    onClick={() => setVisible(true)}
                />
            </div>
            <Dialog
                header="Raw API response"
                visible={visible}
                onHide={() => setVisible(false)}
                style={{ width: '90vw', maxWidth: '900px' }}
                modal
                draggable={false}
            >
                <pre
                    className="surface-100 border-round text-700 p-3 m-0"
                    style={{ maxHeight: '60vh', overflow: 'auto', fontSize: '0.75rem' }}
                >
                    {JSON.stringify(data, null, 2)}
                </pre>
            </Dialog>
        </>
    );
};

// Transitional shim: many consumers still wrap their report sections in
// `<div className="horizontal-report-card">`. Until each of those 25 output
// components is migrated to render `<Card>` directly, this minimal style block
// gives that wrapper the standard surface-card look. New consumers should use
// `<Card>` directly and skip both `<SmartStyles />` and the wrapper div.
export const SmartStyles = () => (
    <style>{`
        .horizontal-report-card {
            background: var(--surface-card);
            border: 1px solid var(--surface-border);
            border-radius: 6px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
            padding: 1rem;
            margin-bottom: 1rem;
        }
    `}</style>
);

export const SmartHeader = ({ title, icon = 'pi-file', remainingBalance, shouldHideCredits }) => (
    <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-2 mb-3">
        <h2 className="text-lg font-semibold text-900 m-0">
            <i className={`pi ${icon} mr-2 text-primary`} aria-hidden="true" />
            {title}
        </h2>
        {!shouldHideCredits && remainingBalance !== undefined && (
            <Tag
                severity="success"
                icon="pi pi-wallet"
                value={`Remaining: ${remainingBalance}`}
                className="text-sm"
            />
        )}
    </div>
);

export const extractResponseData = (reportData) => {
    if (!reportData) return { responseData: null };

    let responseData = null;
    if (reportData.data?.data) responseData = reportData.data.data;
    else if (reportData.report_json?.data?.data) responseData = reportData.report_json.data.data;
    else if (reportData.report_json?.data) responseData = reportData.report_json.data;
    else if (reportData.data && typeof reportData.data === 'object') {
        const { status_code, success, message, message_code, ...rest } = reportData.data;
        responseData = Object.keys(rest).length > 0 ? rest : null;
    }

    const remainingBalance = reportData?.remaining_credits ?? reportData?.data?.remaining_credits;
    const errorMsg = reportData.message || reportData.data?.message || responseData?.message;

    const hasRealData = responseData && Object.entries(responseData).some(
        ([k, v]) =>
            !SP_SKIP.has(k) &&
            v !== null && v !== undefined && v !== '' && v !== false &&
            !(typeof v === 'object' && Object.keys(v).length === 0)
    );

    return { responseData, remainingBalance, errorMsg, hasRealData };
};

export const NoRecordsCard = ({
    errorMsg,
    subtitle = 'No records found for the provided details. Please verify and try again.',
}) => (
    <Card className="text-center">
        <i className="pi pi-search pi-3x text-orange-500 mb-3" aria-hidden="true" />
        <h3 className="text-lg font-semibold text-900 m-0 mb-2">No records found</h3>
        {errorMsg && <p className="text-600 text-sm m-0 mb-2">{errorMsg}</p>}
        <p className="text-600 text-sm m-0">{subtitle}</p>
    </Card>
);
